import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import * as api from "../../api/client";
import type { ApiCtx } from "../../api/client";
import type { LlmCall } from "../../api/types";
import { texte } from "../../texte";

interface GroupedLoop {
  startedAt: string;
  agent: string;
  calls: LlmCall[];
  tokens: {
    total: number;
    input: number;
    output: number;
    cached: number;
    hasDetails: boolean;
  };
  parent?: {
    agent: string;
    loopId: number;
  };
}

/**
 * Laufende LLM-Aufrufe (in-flight): der Server registriert jeden Aufruf beim
 * Absenden (inkl. vollem Prompt) und löscht ihn mit der Antwort — dieser Tab
 * ist also ein Live-Fenster darauf, was die Agenten das Modell GERADE fragen.
 * Während eines Laufs wird im Poll-Takt aktualisiert.
 */
export function Llm({ ctx, running, pollSeconds }: { ctx: ApiCtx; running: boolean; pollSeconds: number }) {
  const query = useQuery({
    queryKey: ["llm", ctx.apiUrl, ctx.token, ctx.workspace],
    queryFn: () => api.llm(ctx),
    refetchInterval: running ? Math.max(2, pollSeconds) * 1000 : false,
  });
  const calls = query.data?.calls ?? [];
  const history = query.data?.history ?? [];

  const [storyGroup, setStoryGroup] = useState<GroupedLoop | null>(null);

  const groupedHistory = useMemo(() => {
    const groups: GroupedLoop[] = [];
    
    for (const call of history) {
      let group = groups.find(g => g.startedAt === call.started_at && g.agent === call.agent);
      if (!group) {
        group = {
          startedAt: call.started_at,
          agent: call.agent,
          calls: [],
          tokens: { total: 0, input: 0, output: 0, cached: 0, hasDetails: false }
        };
        groups.push(group);
      }
      group.calls.push(call);
    }
    
    // Sort loops DESC (newest first)
    groups.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
    // Sort steps within loop DESC (newest first) & compute token summaries
    for (const group of groups) {
      group.calls.sort((a, b) => (b.step ?? 0) - (a.step ?? 0));
      
      let total = 0;
      let input = 0;
      let output = 0;
      let cached = 0;
      let hasDetails = false;
      
      for (const call of group.calls) {
        if (call.token_details) {
          hasDetails = true;
          total += call.token_details.total || 0;
          input += call.token_details.input || 0;
          output += call.token_details.output || 0;
          cached += call.token_details.cached || 0;
        } else if (call.tokens !== undefined) {
          total += call.tokens || 0;
        }
      }
      group.tokens = { total, input, output, cached, hasDetails };
    }

    // Resolve parent loops
    for (let i = 0; i < groups.length; i++) {
      const group = groups[i];
      if (group.agent === "main") continue;
      
      if (
        group.agent === "critic" || 
        group.calls.some(c => c.prompt_name === "goal_check_instructions") ||
        group.calls.some(c => c.prompt_name === "judge_instructions")
      ) {
        const criticCall = group.calls[0];
        if (!criticCall) continue;
        
        let bestParentCall = null;
        let bestDiff = Infinity;
        
        for (const call of history) {
          if (
            call.agent === "critic" || 
            call.prompt_name === "goal_check_instructions" ||
            call.prompt_name === "judge_instructions"
          ) continue;
          
          const parentTime = call.finished_at || call.started_at;
          const criticTime = criticCall.started_at;
          
          if (parentTime && criticTime) {
            const pMs = new Date(parentTime).getTime();
            const cMs = new Date(criticTime).getTime();
            if (!isNaN(pMs) && !isNaN(cMs)) {
              const diff = cMs - pMs;
              if (diff >= 0 && diff < bestDiff) {
                bestDiff = diff;
                bestParentCall = call;
              }
            } else {
              if (parentTime <= criticTime) {
                bestParentCall = call;
              }
            }
          }
        }
        
        if (bestParentCall) {
          const parentGroupIdx = groups.findIndex(g => g.startedAt === bestParentCall.started_at && g.agent === bestParentCall.agent);
          if (parentGroupIdx !== -1) {
            group.parent = {
              agent: groups[parentGroupIdx].agent,
              loopId: groups.length - parentGroupIdx
            };
          }
        }
        continue;
      }
      
      // Look for a parent group that spawned this agent
      for (let j = 0; j < groups.length; j++) {
        if (i === j) continue;
        const potentialParent = groups[j];
        
        let foundSpawn = false;
        for (const call of potentialParent.calls) {
          if (call.tool_calls) {
            for (const tc of call.tool_calls) {
              if (tc.name === "spawn_subagent") {
                try {
                  const args = JSON.parse(tc.arguments);
                  if (args && args.name === group.agent) {
                    foundSpawn = true;
                    break;
                  }
                } catch {
                  const match = tc.arguments.match(/"name"\s*:\s*"([^"]+)"/i);
                  if (match && match[1] === group.agent) {
                    foundSpawn = true;
                    break;
                  }
                }
              }
            }
          }
          if (foundSpawn) break;
        }
        
        if (foundSpawn) {
          group.parent = {
            agent: potentialParent.agent,
            loopId: groups.length - j
          };
          break;
        }
      }
    }

    return groups;
  }, [history]);

  if (!calls.length && !history.length) {
    return (
      <div className="empty" style={{ maxWidth: 560 }}>
        {running ? texte.llm.emptyRunning : texte.llm.emptyIdle}
      </div>
    );
  }

  return (
    <>
      <div className="log-tools">
        <span className="label">{texte.llm.title}</span>
        <span className="hint">{texte.llm.hint}</span>
      </div>
      {calls.length === 0 && (
        <div className="empty" style={{ maxWidth: 560, marginBottom: 20, marginTop: 10 }}>
          {running ? texte.llm.emptyRunning : texte.llm.emptyIdle}
        </div>
      )}
      {calls.map((call) => (
        <LlmCallCard key={call.call_id} call={call} />
      ))}
      
      {history.length > 0 && (
        <>
          <div className="log-tools" style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <span className="label">{texte.llm.historyTitle}</span>
              <span className="hint">{texte.llm.historyHint}</span>
            </div>
            <button className="btn" onClick={() => downloadLlmHistory(groupedHistory)}>{texte.llm.downloadHistory}</button>
          </div>
          {groupedHistory.map((group, gIdx) => {
            const loopId = groupedHistory.length - gIdx;
            const t = group.tokens;
            const meta = getLoopMetadata(group);
            return (
              <div key={`${group.startedAt}-${group.agent}-${gIdx}`} style={{ 
                marginBottom: 24, 
                border: "1px solid var(--line)", 
                borderRadius: "12px", 
                padding: "16px",
                background: "var(--surface)"
              }}>
                <div style={{ 
                  marginBottom: 12, 
                  display: "flex", 
                  justifyContent: "space-between", 
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 12
                }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                    <img 
                      src={meta.avatar} 
                      alt={meta.role} 
                      style={{ 
                        width: 48, 
                        height: 48, 
                        borderRadius: "50%", 
                        border: "1px solid var(--line)",
                        objectFit: "cover"
                      }} 
                    />
                    <div style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ fontSize: 13, fontWeight: "bold", color: "var(--c-text)" }}>
                        {meta.role} (Loop #{loopId}: {group.agent})
                      </span>
                      <span style={{ fontSize: 11, color: "var(--muted)", display: "flex", gap: 8 }}>
                        <span>Gestartet: {group.startedAt}</span>
                        {group.parent && (
                          <span style={{ color: "var(--c-text-sec)" }}>
                            • Parent: Loop #{group.parent.loopId} ({group.parent.agent})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <span style={{ fontSize: 12, color: "var(--c-text-sec)" }}>
                      Tokens: <strong>{t.total.toLocaleString("de-DE")}</strong>
                      {t.hasDetails && ` (In: ${t.input.toLocaleString("de-DE")}, Out: ${t.output.toLocaleString("de-DE")}, Cache: ${t.cached.toLocaleString("de-DE")})`}
                    </span>
                    <button className="btn" style={{ padding: "4px 10px", fontSize: 12 }} onClick={() => setStoryGroup(group)}>Erklären</button>
                  </div>
                </div>
                {group.calls.map((call) => (
                  <LlmCallCard key={call.call_id} call={call} isHistory />
                ))}
              </div>
            );
          })}
        </>
      )}
      {storyGroup && (
        <StoryDialog
          group={storyGroup}
          loopId={groupedHistory.length - groupedHistory.findIndex(g => g.startedAt === storyGroup.startedAt && g.agent === storyGroup.agent)}
          onClose={() => setStoryGroup(null)}
        />
      )}
    </>
  );
}

function LlmCallCard({ call, isHistory }: { call: LlmCall; isHistory?: boolean }) {
  let resultLabelSuffix = "";
  if (isHistory && call.result) {
    try {
      const parsed = JSON.parse(call.result);
      if (parsed && typeof parsed === "object") {
        if (parsed.status && (!parsed.message || String(parsed.message).trim() === "")) {
          resultLabelSuffix = `: ${parsed.status}`;
        }
      }
    } catch {
      // Ignore parse errors
    }
  }

  return (
    <div className="critic-block" style={{ opacity: isHistory ? 0.8 : 1 }}>
      <div className="ch">
        {call.step !== undefined && <span className="cid">Step {call.step}</span>}
        <span className="cid">{call.agent}</span>
        <span className="tgt">{call.model}</span>
        <span className="tgt">
          {texte.llm.template} {call.prompt_name}
        </span>
        <span className="tgt">
          {isHistory ? texte.llm.finishedAt : texte.llm.since} {isHistory ? call.finished_at : call.started_at}
        </span>
        {call.token_details ? (
          <span className="tgt">
            Tokens: {call.token_details.total} (Input: {call.token_details.input}, Cached: {call.token_details.cached ?? 0}, Output: {call.token_details.output})
          </span>
        ) : (
          call.tokens !== undefined && (
            <span className="tgt">
              Tokens: {call.tokens}
            </span>
          )
        )}
      </div>
      <details>
        <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.instructions}</summary>
        <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
          {call.instructions || texte.llm.emptyField}
        </div>
      </details>
      <details>
        <summary style={{ cursor: "pointer", fontSize: 13 }}>{texte.llm.input}</summary>
        <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
          {call.input || texte.llm.emptyField}
        </div>
      </details>
      {call.tool_calls && call.tool_calls.length > 0 && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>
            {texte.llm.toolCalls}
            {call.tool_calls && call.tool_calls.length > 0 && (
              <>: {call.tool_calls.map((tc) => {
                let docId: string | undefined;
                try {
                  const parsed = JSON.parse(tc.arguments);
                  docId = parsed?.doc_id || parsed?.docid || parsed?.docId || parsed?.document_id || parsed?.documentId;
                } catch {
                  const match = tc.arguments.match(/"doc(?:_)?id"\s*:\s*"([^"]+)"/i);
                  if (match) docId = match[1];
                }
                return docId ? `${tc.name}(${docId})` : tc.name;
              }).join(", ")}</>
            )}
          </summary>
          {call.tool_calls.map((tc, idx) => {
            let content = tc.arguments;
            if (tc.name === "run_python") {
              try {
                const args = JSON.parse(tc.arguments);
                if (args.code) {
                  content = args.code;
                }
              } catch {
                // Ignore parse errors
              }
            }
            return (
              <div key={idx} style={{ marginTop: 8 }}>
                <strong style={{ fontSize: 13 }}>{tc.name}</strong>
                <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 4 }}>
                  {content}
                </div>
              </div>
            );
          })}
        </details>
      )}
      {isHistory && (
        <details>
          <summary style={{ cursor: "pointer", fontSize: 13 }}>
            {texte.llm.result}
            {resultLabelSuffix}
          </summary>
          <div className="logbox" style={{ whiteSpace: "pre-wrap", marginTop: 6 }}>
            {call.result || texte.llm.emptyField}
          </div>
        </details>
      )}
    </div>
  );
}

function getLoopMetadata(group: GroupedLoop) {
  if (group.agent === "critic") {
    return {
      avatar: "/kritiker.jpg",
      role: "Interner Kritiker"
    };
  }
  if (group.agent === "planner") {
    return {
      avatar: "/planer.jpg",
      role: "Planer"
    };
  }
  if (group.calls.some(c => c.prompt_name === "goal_check_instructions")) {
    return {
      avatar: "/zielchecker.jpg",
      role: "Ziel-Checker"
    };
  }
  if (group.calls.some(c => c.prompt_name === "judge_instructions")) {
    return {
      avatar: "/entscheider.jpg",
      role: "Richter (LLM-Judge)"
    };
  }
  if (group.agent === "main") {
    return {
      avatar: "/agent.jpg",
      role: "Haupt-Agent"
    };
  }
  return {
    avatar: "/agent.jpg",
    role: `Sub-Agent (${group.agent})`
  };
}

function downloadLlmHistory(groups: GroupedLoop[]) {
  let md = "# LLM History\n\n";

  // Sort loops ASC (oldest first)
  const ascendingGroups = [...groups].sort((a, b) => a.startedAt.localeCompare(b.startedAt));

  for (let i = 0; i < ascendingGroups.length; i++) {
    const group = ascendingGroups[i];
    const loopId = i + 1;
    const tokenInfo = group.tokens;
    const meta = getLoopMetadata(group);
    md += `## Loop #${loopId}: ${group.agent} (${meta.role})\n`;
    if (group.parent) {
      md += `- **Parent Loop:** Loop #${group.parent.loopId} (${group.parent.agent})\n`;
    }
    md += `- **Started At:** ${group.startedAt}\n`;
    if (tokenInfo.hasDetails) {
      md += `- **Total Loop Tokens:** ${tokenInfo.total} (Input: ${tokenInfo.input}, Cached: ${tokenInfo.cached}, Output: ${tokenInfo.output})\n`;
    } else {
      md += `- **Total Loop Tokens:** ${tokenInfo.total}\n`;
    }
    md += `\n`;
    
    // Sort steps ASC (oldest first)
    const ascendingCalls = [...group.calls].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
    
    for (const call of ascendingCalls) {
      md += `### Step ${call.step ?? "?"} (${call.model})\n`;
      md += `- **Template:** ${call.prompt_name}\n`;
      md += `- **Finished At:** ${call.finished_at || ""}\n`;
      if (call.token_details) {
        md += `- **Tokens:** ${call.token_details.total} (Input: ${call.token_details.input}, Cached: ${call.token_details.cached ?? 0}, Output: ${call.token_details.output})\n`;
      } else if (call.tokens !== undefined) {
        md += `- **Tokens:** ${call.tokens}\n`;
      }
      md += `\n#### Instructions\n${call.instructions}\n\n`;
      if (call.input) {
        md += `#### Input\n${call.input}\n\n`;
      }
      
      if (call.tool_calls && call.tool_calls.length > 0) {
        md += `#### Tool Calls\n`;
        for (const tc of call.tool_calls) {
          md += `##### ${tc.name}\n`;
          let content = tc.arguments;
          if (tc.name === "run_python") {
            try {
              const args = JSON.parse(tc.arguments);
              if (args.code) {
                content = args.code;
              }
            } catch {
              // Ignore parse errors
            }
            md += "```python\n" + content + "\n```\n\n";
          } else {
            md += "```json\n" + content + "\n```\n\n";
          }
        }
      }
      if (call.result) {
        md += `#### Result\n${call.result}\n\n`;
      }
      md += `---\n\n`;
    }
  }

  const blob = new Blob([md], { type: "text/markdown" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `llm_history_${new Date().toISOString().replace(/[:.]/g, "-")}.md`;
  a.click();
  URL.revokeObjectURL(url);
}

function StoryDialog({ group, loopId, onClose }: { group: GroupedLoop; loopId: number; onClose: () => void }) {
  const chronologicalCalls = useMemo(() => {
    return [...group.calls].sort((a, b) => (a.step ?? 0) - (b.step ?? 0));
  }, [group.calls]);

  const meta = getLoopMetadata(group);

  return (
    <div style={{
      position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: "rgba(0,0,0,0.6)", zIndex: 1000,
      display: "flex", justifyContent: "center", alignItems: "center"
    }}>
      <div style={{
        backgroundColor: "#ffffff",
        color: "#000000",
        padding: "20px", borderRadius: "12px",
        width: "90%", maxWidth: "800px", maxHeight: "85vh",
        overflowY: "auto", position: "relative",
        boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
        border: "1px solid var(--border, #444)"
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, position: "sticky", top: -20, backgroundColor: "#ffffff", zIndex: 10, paddingTop: 20, paddingBottom: 10, borderBottom: "1px solid #ddd" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <img 
              src={meta.avatar} 
              alt={meta.role} 
              style={{ 
                width: 60, 
                height: 60, 
                borderRadius: "50%", 
                border: "1px solid #ddd",
                objectFit: "cover"
              }} 
            />
            <div>
              <h2 style={{ margin: 0, fontSize: 18, color: "#333" }}>
                {meta.role} (Loop #{loopId}: {group.agent})
              </h2>
              <div style={{ fontSize: 12, color: "#666", marginTop: 4 }}>
                Tokens: {group.tokens.total}
                {group.tokens.hasDetails && ` (Input: ${group.tokens.input}, Cached: ${group.tokens.cached}, Output: ${group.tokens.output})`}
                {group.parent && ` | Parent: Loop #${group.parent.loopId} (${group.parent.agent})`}
              </div>
            </div>
          </div>
          <button className="btn" onClick={onClose}>Schließen</button>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
          {chronologicalCalls.map((call, idx) => {
             const bubbles = [];
             
             // User Message
             if (idx === 0) {
               bubbles.push(
                 <div key={`${call.call_id}-u1`} style={{ alignSelf: "flex-end", backgroundColor: "rgba(0, 120, 255, 0.15)", padding: "12px 16px", borderRadius: "16px 16px 0 16px", maxWidth: "85%", border: "1px solid rgba(0, 120, 255, 0.3)" }}>
                   <div style={{ fontSize: 12, fontWeight: "bold", opacity: 0.7, marginBottom: 4 }}>User</div>
                   <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                     <strong>Anweisungen:</strong>
                     <div style={{ marginTop: 4, marginBottom: 12, opacity: 0.9 }}>{call.instructions}</div>
                     <strong>Eingabe:</strong>
                     <div style={{ marginTop: 4, opacity: 0.9 }}>{call.input}</div>
                   </div>
                 </div>
               );
             } else {
               let formattedInput: React.ReactNode = call.input;
               try {
                 const parsed = JSON.parse(call.input);
                 if (Array.isArray(parsed)) {
                   formattedInput = (
                     <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                       {parsed.map((item, i) => (
                         <div key={i}>
                           {item.output !== undefined ? item.output : JSON.stringify(item)}
                         </div>
                       ))}
                     </div>
                   );
                 }
               } catch {}

               bubbles.push(
                 <div key={`${call.call_id}-u`} style={{ alignSelf: "flex-end", backgroundColor: "rgba(0, 120, 255, 0.15)", padding: "12px 16px", borderRadius: "16px 16px 0 16px", maxWidth: "85%", border: "1px solid rgba(0, 120, 255, 0.3)" }}>
                   <div style={{ fontSize: 12, fontWeight: "bold", opacity: 0.7, marginBottom: 4 }}>User</div>
                   <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                     Ich habe das für dich erledigt, hier sind die Ergebnisse:
                     <div style={{ marginTop: 12, opacity: 0.9 }}>{formattedInput}</div>
                   </div>
                 </div>
               );
             }

             // Assistant Message
             let assistantText = "";
             if (call.tool_calls && call.tool_calls.length > 0) {
               if (idx === 0) {
                 assistantText = "Ich möchte folgende Werkzeuge verwenden. Kannst du das für mich tun?\n\n";
               } else {
                 assistantText = "Danke. Basierend auf diesen Antworten habe ich mehr Arbeit für dich. Ich möchte folgende Werkzeuge verwenden:\n\n";
               }
               assistantText += call.tool_calls.map(tc => {
                 let content = tc.arguments;
                 if (tc.name === "run_python") {
                   try {
                     const args = JSON.parse(tc.arguments);
                     if (args.code) { content = args.code; }
                   } catch {}
                 }
                 return `• **${tc.name}**:\n${content}`;
               }).join("\n\n");
             } else {
               assistantText = `Ich bin fertig. Hier ist mein Ergebnis:\n\n${call.result || ""}`;
             }

             bubbles.push(
               <div key={`${call.call_id}-a`} style={{ alignSelf: "flex-start", backgroundColor: "rgba(128, 128, 128, 0.15)", padding: "12px 16px", borderRadius: "16px 16px 16px 0", maxWidth: "85%", border: "1px solid rgba(128, 128, 128, 0.3)" }}>
                 <div style={{ fontSize: 12, fontWeight: "bold", opacity: 0.7, marginBottom: 4 }}>Assistant</div>
                 <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>
                   {assistantText}
                 </div>
               </div>
             );

             return bubbles;
          })}
        </div>
      </div>
    </div>
  );
}
